import React from 'react';
import {FormField, FormSection} from '../../../Presentation/components/common';
import {createFieldProps} from '../../../Presentation/utils/formUtils';
import {Brand} from '../models/brand.model';
import {Tag} from '../models/tag.model';
import {ProductType} from '../models/productType.model';
import {FormState, FormActions} from '../../../Presentation/utils/formUtils';
import ProductImagesPreview from './ProductImagesPreview';
import {UnifiedSerialNumberInput} from '../../serialNumbers';

interface ProductFormFieldsProps {
  formState: FormState<any>;
  formActions: FormActions<any>;
  imagePreviews: string[];
  onImageRemove: (index: number) => void;
  brands: Brand[];
  tags: Tag[];
  productTypes: ProductType[];
  brandsLoading: boolean;
  tagsLoading: boolean;
  typesLoading: boolean;
  allowStockChanges: boolean;
  canEditHasSerial: boolean;
}

/**
 * Reusable component for product form fields
 * Follows single responsibility principle
 */
export const ProductFormFields: React.FC<ProductFormFieldsProps> = ({
  formState,
  formActions,
  imagePreviews,
  onImageRemove,
  brands,
  tags,
  productTypes,
  brandsLoading,
  tagsLoading,
  typesLoading,
  allowStockChanges,
  canEditHasSerial
}) => {
  return (
    <>
      {/* Basic Product Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
        <FormField
          type="text"
          label="اسم المنتج *"
          placeholder="أدخل اسم المنتج"
          {...createFieldProps('name', formState, formActions)}
        />
        <FormField
          type="select"
          label="نوع المنتج *"
          options={productTypes.map(t => ({value: t.id, label: t.name}))}
          disabled={typesLoading}
          {...createFieldProps('type', formState, formActions)}
        />
        <FormField
          type="select"
          label="البراند *"
          options={brands.map((b: Brand) => ({value: b.id, label: b.name}))}
          disabled={brandsLoading}
          {...createFieldProps('brandId', formState, formActions)}
        />
        <FormField
          type="select"
          label="العلامات"
          options={tags.map((tag: Tag) => ({value: tag.id, label: tag.name}))}
          disabled={tagsLoading}
          {...createFieldProps('selectedTag', formState, formActions)}
        />
        <FormField
          type="number"
          label="السعر *"
          placeholder="أدخل السعر"
          {...createFieldProps('price', formState, formActions)}
        />
        <FormField
          type="text"
          label="الباركود"
          placeholder="أدخل الباركود"
          {...createFieldProps('barcode', formState, formActions)}
        />
        <FormField
          type="file"
          label="رفع صور المنتج *"
          accept="image/*"
          multiple
          {...createFieldProps('images', formState, formActions)}
        />
        {canEditHasSerial && (
          <div className="flex items-center h-full mt-6 md:mt-0 justify-end">
            <FormField
              type="checkbox"
              label="هل المنتج يحتوي على سيريال؟"
              {...createFieldProps('hasSerial', formState, formActions)}
            />
          </div>
        )}
      </div>

      {/* Image Previews */}
      {console.log('ProductFormFields - imagePreviews:', imagePreviews, 'formState.data.images:', formState.data.images)}
      {imagePreviews.length > 0 && (
        <ProductImagesPreview
          images={Array.isArray(formState.data.images) ? formState.data.images : []}
          onRemove={onImageRemove}
        />
      )}

      {/* Stock Management */}
      {allowStockChanges && (
        <FormSection title="الكمية الابتدائية">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              type="number"
              label={formState.data.hasSerial ? "الكمية (تحسب تلقائياً)" : "الكمية الابتدائية"}
              placeholder={formState.data.hasSerial ? "تحسب تلقائياً حسب عدد السيريالات" : "أدخل الكمية الابتدائية"}
              min="0"
              {...createFieldProps('quantity', formState, formActions)}
              disabled={formState.data.hasSerial}
            />
            <div className="flex items-center text-sm text-gray-600 bg-brand-50 p-3 rounded-lg">
              <svg className="w-5 h-5 text-brand-600 ml-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              {formState.data.hasSerial
                ? "الكمية تحسب تلقائياً حسب عدد السيريالات المضافة"
                : "هذه الكمية ستكون متاحة في المخزون فور إنشاء المنتج"
              }
            </div>
          </div>
        </FormSection>
      )}

      {/* Serial Numbers */}
      {allowStockChanges && formState.data.hasSerial && (
        <UnifiedSerialNumberInput
          value={formState.data.serials}
          onChange={(serials) => formActions.setField('serials', serials)}
          error={formState.errors.serials}
          context="product"
          required={false}
          title="السيريالات الابتدائية (اختياري)"
          warningMessage="يمكنك إنشاء المنتج بدون سيريالات ابتدائية وإضافتها لاحقاً من خلال إدارة المخزون"
        />
      )}
    </>
  );
};

export default ProductFormFields;
